import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { format } from 'date-fns';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import CalendarIcon from '@atlaskit/icon/core/calendar';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { DatePicker } from '@atlaskit/datetime-picker';
import Tooltip from '@atlaskit/tooltip';

import { AnalyticsPillButton } from '../common/AnalyticsPillButton';

const i18n = defineMessages({
	customExpanded: {
		id: 'confluence-analytics.filters.date-option-custom-expanded-form',
		defaultMessage: 'Custom date range',
		description: 'Expanded form of custom to display in a tooltip.',
	},
	calendarIconLabel: {
		id: 'confluence-analytics.filters.date-option-cal-icon-label',
		defaultMessage: 'Calendar icon',
		description: 'Non-visible aria label for the calendar icon button that opens the calendar.',
	},
	expandIconLabel: {
		id: 'confluence-analytics.filters.date-option-expand-icon-label',
		defaultMessage: 'Expand icon',
		description: 'Non-visible aria label for the expand icon button that opens the calendar.',
	},
});

type CalendarPickerProps = {
	isSelected: boolean;
	startDateDefaultvalue: Date;
	startDateValue: Date;
	startDateMin: Date;
	startDateMax: Date;
	onStartDateUpdate: (value: string) => void;
	endDateDefaultvalue: Date;
	endDateValue: Date;
	endDateMin: Date;
	endDateMax: Date;
	onEndDateUpdate: (value: string) => void;
};

const datePickerContainerStyles = xcss({
	padding: 'space.050',
});

const calendarContainerStyles = xcss({ zIndex: 'modal' });
const calendarIconContainerStyles = xcss({ height: '100%' });

export const CalendarPicker = ({
	isSelected,
	startDateDefaultvalue,
	startDateValue,
	startDateMin,
	startDateMax,
	onStartDateUpdate,
	endDateDefaultvalue,
	endDateValue,
	endDateMin,
	endDateMax,
	onEndDateUpdate,
}: CalendarPickerProps) => {
	const { formatMessage } = useIntl();
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);

	return (
		<Box as="div" xcss={calendarContainerStyles}>
			<Popup
				shouldRenderToParent
				isOpen={isCalendarOpen}
				onClose={() => setIsCalendarOpen(false)}
				placement="bottom"
				content={() => (
					<Inline
						alignBlock="center"
						space="space.100"
						separator="-"
						xcss={datePickerContainerStyles}
					>
						<DatePicker
							appearance="subtle"
							value={startDateValue.toISOString()}
							minDate={startDateMin.toISOString()}
							maxDate={startDateMax.toISOString()}
							onChange={(value) => {
								// There may be cases where value is nullish, i.e. select's clear state
								onStartDateUpdate(value || startDateDefaultvalue.toISOString());
							}}
							shouldShowCalendarButton
						/>
						<DatePicker
							appearance="subtle"
							value={endDateValue.toISOString()}
							minDate={endDateMin.toISOString()}
							maxDate={endDateMax.toISOString()}
							onChange={(value) => {
								onEndDateUpdate(value || endDateDefaultvalue.toISOString());
							}}
							shouldShowCalendarButton
						/>
					</Inline>
				)}
				trigger={(triggerProps) => (
					<Tooltip content={formatMessage(i18n.customExpanded)}>
						<AnalyticsPillButton
							{...triggerProps}
							onClick={() => setIsCalendarOpen(!isCalendarOpen)}
							isSelected={isCalendarOpen || isSelected}
						>
							<Flex alignItems="center" xcss={calendarIconContainerStyles}>
								<CalendarIcon label={formatMessage(i18n.calendarIconLabel)} color="currentColor" />
								{isSelected && (
									<Box paddingInline="space.100">
										{format(startDateValue, 'MMM dd, yyyy')} -{' '}
										{format(endDateValue, 'MMM dd, yyyy')}
									</Box>
								)}
								<ChevronDownIcon
									label={formatMessage(i18n.expandIconLabel)}
									color="currentColor"
									spacing="compact"
									size="small"
								/>
							</Flex>
						</AnalyticsPillButton>
					</Tooltip>
				)}
			/>
		</Box>
	);
};
